// Asset preview — ENEMIES and BOSSES tabs. Enemies: one row per enemy type with
// every registered animation playing at gameplay size plus an elite variant
// (outline mask frames tinted per affix, elite scale). Bosses: every boss_*
// texture and animation in a paged gallery.

import Phaser from 'phaser';
import { COLORS, css } from '../../config/palette';
import { ELITE_AFFIXES, ENEMY_IDS, type EliteAffix } from '../../core/types';
import { ELITE, ELITE_AFFIXES_DEF } from '../../data/enemies';
import { foeRig } from '../../art/foes';
import { ensureHeartTextures } from '../../systems/bosses';
import { Gallery, animsForTexture, frameNames, texturesWithPrefix, type GalleryEntry } from './gallery';
import type { PreviewTab } from './tab';
import { PV, drawPanel, idLabel, label } from './widgets';

const ENEMY_ACCENT = COLORS.threat;

interface EnemySprite {
  s: Phaser.GameObjects.Sprite;
  anim: string;
  loop: boolean;
  wait: number;
  /** Elite outline that mirrors the body's frame. */
  outline?: Phaser.GameObjects.Sprite;
}

export class EnemyTab implements PreviewTab {
  readonly root: Phaser.GameObjects.Container;
  private sprites: EnemySprite[] = [];
  private gallery: Gallery;
  private affixText: Phaser.GameObjects.Text[] = [];
  private clock = 0;
  private affixIndex = 0;

  constructor(private scene: Phaser.Scene) {
    this.root = scene.add.container(0, 0);
    const x0 = PV.pad;
    const y0 = PV.headerH + 14;
    const w = PV.W - PV.pad * 2;
    const rowH = 182;
    const g = scene.add.graphics();
    this.root.add(g);

    ENEMY_IDS.forEach((id, row) => {
      const key = `enemy_${id}`;
      const y = y0 + row * (rowH + 8);
      drawPanel(g, x0, y, w, rowH, { fill: COLORS.uiBg, fillAlpha: 0.75, chamfer: 10, accent: ENEMY_ACCENT });
      this.root.add(label(scene, x0 + 18, y + 14, id.toUpperCase(), { font: 'display', size: 22, weight: 700, spacing: 4 }));
      this.root.add(label(scene, x0 + 18, y + 44, key, { font: 'mono', size: 11, color: COLORS.uiDim }));
      if (!scene.textures.exists(key)) {
        this.root.add(label(scene, x0 + 220, y + rowH / 2, 'TEXTURE NOT REGISTERED', { font: 'display', size: 16, color: COLORS.uiDim, originY: 0.5, spacing: 3 }));
        return;
      }
      const order = ['idle', 'move', 'windup', 'attack', 'stagger', 'leap'];
      const rank = (k: string): number => {
        const i = order.indexOf(k.slice(key.length + 1));
        return i < 0 ? order.length : i;
      };
      const anims = animsForTexture(scene, key).sort((a, b) => rank(a.key) - rank(b.key));
      this.root.add(label(scene, x0 + 18, y + 62, `${anims.length} ANIMS · ${frameNames(scene, key).length} FRAMES`, { font: 'mono', size: 11, color: COLORS.uiDim }));
      const cellW = 200;
      const boxH = rowH - 52;
      anims.forEach((a, i) => {
        const cx = x0 + 230 + i * cellW + cellW / 2;
        this.addSprite(key, a, cx, y + 16, boxH, cellW - 20, 1);
        this.root.add(label(scene, cx, y + rowH - 26, a.key.replace(`${key}_`, '').toUpperCase(), { font: 'mono', size: 11, originX: 0.5 }));
        this.root.add(label(scene, cx, y + rowH - 13, `${a.frames.length}f ${a.frameRate}fps${a.repeat === -1 ? ' loop' : ''}`, { font: 'mono', size: 10, color: COLORS.uiDim, originX: 0.5 }));
      });
      // Elite variant: idle (or first anim) at elite scale with the tinted outline mask.
      const idle = anims.find((a) => a.key.endsWith('_idle')) ?? anims[0];
      if (idle) {
        const cx = x0 + w - 150;
        g.lineStyle(1, COLORS.uiBorder, 0.7);
        g.lineBetween(cx - 130, y + 14, cx - 130, y + rowH - 14);
        this.addSprite(key, idle, cx, y + 16, boxH, 240, ELITE.scale, true);
        const t = label(scene, cx, y + rowH - 26, 'ELITE', { font: 'display', size: 12, weight: 700, originX: 0.5, spacing: 3 });
        this.affixText.push(t);
        this.root.add(t);
      }
    });

    // Every other enemy texture (shared FX, shields, debris) as a gallery strip.
    const others: GalleryEntry[] = [];
    for (const k of texturesWithPrefix(scene, ['enemy_'])) {
      const isSheet = ENEMY_IDS.some((id) => k === `enemy_${id}`);
      if (!isSheet) {
        const frames = frameNames(scene, k).filter((n) => !n.endsWith('_o'));
        // Multi-frame textures (the bulwark shield: up / bash / down) show every frame, not the strip.
        if (frames.length > 1) for (const f of frames) others.push({ key: k, frame: f, label: `${k.replace('enemy_', '')}:${f}` });
        else others.push({ key: k, label: k.replace('enemy_', ''), sub: k === 'enemy_placeholder' ? 'fallback only' : undefined, add: k.includes('fx') });
        continue;
      }
      // Static parts of the sheets that no animation shows: debris, gun arm.
      for (const f of frameNames(scene, k).filter((n) => /^(gib|gun)/.test(n) && !n.endsWith('_o'))) {
        others.push({ key: k, frame: f, label: `${k.replace('enemy_', '')}:${f}` });
      }
    }
    const gy = y0 + 4 * (rowH + 8);
    this.gallery = new Gallery(scene, others, { x: x0, y: gy, w, h: PV.H - PV.pad - gy, cellW: 120, cellH: 116, maxScale: 1.5, accent: ENEMY_ACCENT, title: 'PARTS · FX · DEBRIS' });
    this.root.add(this.gallery.root);
    this.applyAffix();
  }

  private addSprite(key: string, a: Phaser.Animations.Animation, cx: number, top: number, boxH: number, boxW: number, scale: number, elite = false): void {
    const s = this.scene;
    const spr = s.add.sprite(cx, 0, key, a.frames[0].frame.name);
    const fw = spr.frame.realWidth || 1;
    const fh = spr.frame.realHeight || 1;
    const fit = Math.min(scale, (boxW / fw) * scale, (boxH / fh) * scale);
    spr.setScale(fit).setOrigin(0.5, 1);
    spr.setY(top + boxH - 4);
    let outline: Phaser.GameObjects.Sprite | undefined;
    if (elite) {
      outline = s.add.sprite(spr.x, spr.y, key, a.frames[0].frame.name).setOrigin(0.5, 1).setScale(fit);
      this.root.add(outline);
    }
    this.root.add(spr);
    spr.play(a.key);
    this.sprites.push({ s: spr, anim: a.key, loop: a.repeat === -1, wait: 0, outline });
  }

  private applyAffix(): void {
    const affix: EliteAffix = ELITE_AFFIXES[this.affixIndex % ELITE_AFFIXES.length];
    const def = ELITE_AFFIXES_DEF[affix];
    for (const t of this.affixText) t.setText(`ELITE · ${def?.name ?? idLabel(affix)}`).setColor(css(def?.color ?? COLORS.threat));
    for (const it of this.sprites) it.outline?.setTint(def?.color ?? COLORS.threat);
  }

  update(dt: number): void {
    this.clock += dt;
    if (this.clock > 1.6) {
      this.clock = 0;
      this.affixIndex++;
      this.applyAffix();
    }
    for (const it of this.sprites) {
      if (!it.loop && !it.s.anims.isPlaying) {
        it.wait += dt;
        if (it.wait > 0.7) {
          it.wait = 0;
          it.s.play(it.anim);
        }
      }
      if (it.outline) {
        const name = `${it.s.frame.name}_o`;
        if (it.s.texture.has(name)) it.outline.setFrame(name).setVisible(true);
        else it.outline.setVisible(false);
      }
    }
    this.gallery.update(dt);
  }

  key(code: string): boolean {
    if (code === 'BracketRight' || code === 'PageDown') this.gallery.flip(1);
    else if (code === 'BracketLeft' || code === 'PageUp') this.gallery.flip(-1);
    else return false;
    return true;
  }

  destroy(): void {
    this.root.destroy();
  }
}

export class BossTab implements PreviewTab {
  readonly root: Phaser.GameObjects.Container;
  private gallery: Gallery;
  private animGallery: Gallery;

  constructor(scene: Phaser.Scene) {
    this.root = scene.add.container(0, 0);
    // The Heart's textures are otherwise only built when its fight starts.
    ensureHeartTextures(scene);
    const flameTint = foeRig(scene, 'titan') ? 0xff9a40 : undefined;
    const keys = texturesWithPrefix(scene, ['boss_']);
    const anims: GalleryEntry[] = [];
    const statics: GalleryEntry[] = [];
    for (const k of keys) {
      const tex = scene.textures.get(k);
      const frames = frameNames(scene, k);
      const src = tex.getSourceImage() as { width?: number; height?: number };
      statics.push({ key: k, label: k.replace('boss_', ''), sub: `${src?.width ?? '?'}×${src?.height ?? '?'}${frames.length > 1 ? ` · ${frames.length}f` : ''}`, add: /glow|flame|halo/.test(k), tint: k.includes('flame') ? flameTint : undefined });
      for (const a of animsForTexture(scene, k)) anims.push({ key: k, anim: a.key, label: a.key.replace('boss_', ''), sub: `${a.frames.length}f ${a.frameRate}fps` });
    }
    const x = PV.pad;
    const y = PV.headerH + 14;
    const w = PV.W - PV.pad * 2;
    const animH = anims.length ? 300 : 0;
    this.gallery = new Gallery(scene, statics, {
      x,
      y,
      w,
      h: PV.H - PV.pad - y - (animH ? animH + 10 : 0),
      cellW: 196,
      cellH: 196,
      maxScale: 1,
      accent: COLORS.threatHot,
      title: `BOSS PARTS · ${keys.length} TEXTURES`,
    });
    this.animGallery = new Gallery(scene, anims, { x, y: PV.H - PV.pad - animH, w, h: animH || 1, cellW: 220, cellH: 230, maxScale: 1, accent: COLORS.threatHot, title: 'BOSS ANIMATIONS' });
    this.animGallery.root.setVisible(animH > 0);
    this.root.add([this.gallery.root, this.animGallery.root]);
  }

  update(dt: number): void {
    this.animGallery.update(dt);
  }

  key(code: string): boolean {
    if (code === 'BracketRight' || code === 'PageDown') this.gallery.flip(1);
    else if (code === 'BracketLeft' || code === 'PageUp') this.gallery.flip(-1);
    else return false;
    return true;
  }

  destroy(): void {
    this.root.destroy();
  }
}

/** Generic gallery tab (UI icons). */
export class IconTab implements PreviewTab {
  readonly root: Phaser.GameObjects.Container;
  private gallery: Gallery;

  constructor(scene: Phaser.Scene) {
    this.root = scene.add.container(0, 0);
    const entries = texturesWithPrefix(scene, ['icon_', 'ui_', 'art_']).map((k) => ({ key: k, label: k.replace(/^icon_/, '') }));
    this.gallery = new Gallery(scene, entries, {
      x: PV.pad,
      y: PV.headerH + 14,
      w: PV.W - PV.pad * 2,
      h: PV.H - PV.headerH - 34,
      cellW: 150,
      cellH: 132,
      maxScale: 2,
      accent: COLORS.ultBar,
      title: 'INTERFACE ICONS · KEY ART',
    });
    this.root.add(this.gallery.root);
  }

  update(): void {}

  key(code: string): boolean {
    if (code === 'BracketRight' || code === 'PageDown') this.gallery.flip(1);
    else if (code === 'BracketLeft' || code === 'PageUp') this.gallery.flip(-1);
    else return false;
    return true;
  }

  destroy(): void {
    this.root.destroy();
  }
}
